import { useMemo } from 'react';
import { Product } from '../../api/contracts/catalog/types';
import { useBundle } from '../../bundles/useBundleService';
import { calculateDeviceRequirementAnnual } from '../helper';
import { useBundledProducts, useStockParameters } from '../useCustomerCatalogueService';

export interface StockBinLevel {
  product: Product;
  minStockLevel: number;
  maxStockLevel: number;
  recMinStockLevel?: number;
  recMaxStockLevel?: number;
  reservedStock?: number;
}

export const useDefaultBundleStockLevels = () => {
  const bundledProducts = useBundledProducts();
  const { stockParameters, bundleWeightings } = useStockParameters();
  const bundles = useBundle(s => s.bundles);

  return useMemo(() => {
    const devicesPerQtr =
      (calculateDeviceRequirementAnnual(
        stockParameters.numberOfEmployees!,
        stockParameters.churnRatePercentage!,
        stockParameters.gsdPercentage!
      ) || 0) / 4;

    const stockLevels: StockBinLevel[] = bundledProducts.map(product => {
      const weightingPercentage = bundleWeightings.reduce((prevTotal, bundleWeighting) => {
        const bundle = bundles.find(b => b.id === bundleWeighting.bundleId);

        if (!bundle) {
          return prevTotal;
        } else {
          if (bundle.mainDevice === product.id || !!bundle.bundledItems.find(i => i.catalogItem === product.id)) {
            return prevTotal + (bundleWeighting.weightingPercentage || 0);
          }
        }

        return prevTotal;
      }, 0);

      const projectedRequirement = (devicesPerQtr * weightingPercentage) / 100;

      const minStockLevel = Math.ceil(projectedRequirement * 0.75);
      const projectedMaxStockLevel = Math.ceil(projectedRequirement * 1.25);
      const maxStockLevel =
        projectedMaxStockLevel === minStockLevel && minStockLevel > 0 ? minStockLevel + 1 : projectedMaxStockLevel;

      return {
        product,
        maxStockLevel,
        minStockLevel,
        recMaxStockLevel: maxStockLevel,
        recMinStockLevel: minStockLevel
      };
    });

    return stockLevels;
  }, [bundledProducts, stockParameters, bundleWeightings, bundles]);
};
